<script lang="ts" setup>
import { VSelect, VBtn, VMenu, VList, VListItem } from 'vuetify/components';
import { useI18n } from 'vue-i18n';
import { ProjectStatus, type Project } from '@/gql/graphql';
import { ProjectStatus2 } from '@/types';
import ProjectDeleteDialog from './ProjectDeleteDialog.vue';
import ProjectConvertModal from './ProjectConvertModal.vue';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useMutation } from '@vue/apollo-composable';
import { graphql } from '@/gql';
import { useRouteParams } from '@vueuse/router';
import { useNotificationStore } from '@/stores/notification';
import { usePermissions } from '@/composables/permissions';
import confetti from 'canvas-confetti';
import { PolicyAction } from '@/types';
import { RouteName } from '@/router';
import { useUserSettingsStore } from '@/stores/user-settings';
import SubprojectConvertDialog from './SubprojectConvertDialog.vue';
import ProjectMoveModal from './ProjectMoveModal.vue';
import ProjectHoldModal from './ProjectHoldModal.vue';
import type { ProjectHoldFormValues } from './ProjectHoldForm.vue';

const {
    loading,
    project,
    isSubproject,
    focusId = '',
    needId = '',
} = defineProps<{
    loading?: boolean;
    project: Project;
    isSubproject?: boolean;
    focusId?: string;
    needId?: string;
}>();
const emit = defineEmits<{ (e: 'edit'): void }>();

const { hasPermission } = usePermissions();

const router = useRouter();

const notificationStore = useNotificationStore();
const projectId = useRouteParams('id') ?? '';
const userSettingsStore = useUserSettingsStore();
const activePortfolioId = computed(
    () => userSettingsStore.activePortfolio?.id || ''
);

const { t } = useI18n({
    messages: {
        nl: {
            on_hold: 'On Hold',
            finished: 'Afgerond',
            active: 'Actief',
            edit: 'Bewerken',
            delete: 'Verwijderen',
            convert: 'Omzetten naar @.lower:subproject',
            convert_back: 'Omzetten naar @.lower:project',
            move: '@:portfolio wijzigen',
            status_updated: 'Omgezet naar {status}',
            project_finished: '@:project is afgerond 🥳',
            project_on_hold: '@:project is on-hold gezet',
            project_active: '@:project is actief',
            error_updating_status: 'Updaten naar {status} mislukt',
        },
        en: {
            on_hold: 'On Hold',
            finished: 'Finished',
            active: 'Active',
            edit: 'Edit',
            delete: 'Delete',
            convert: 'Convert to @.lower:subproject',
            convert_back: 'Convert to @.lower:project',
            move: 'Change @.lower:portfolio',
            status_updated: 'Type is now {status}',
            project_finished: '@:project finished 🥳',
            project_on_hold: '@:project has been put on hold',
            project_active: '@:project is active',
            error_updating_status: 'Failed to set to {status}',
        },
    },
});

const isShowingHoldModal = ref(false);

function onProjectDeleted() {
    router.push({
        name: RouteName['dashboard.projects'],
    });
}

const status = defineModel<ProjectStatus>('status');
const status2 = defineModel<ProjectStatus2>('status2');

const statusOptions: {
    value: 'approved_idea' | 'idea' | 'project';
    title: string;
}[] = [
    {
        title: t('project'),
        value: 'project',
    },
    {
        title: t('approved_idea'),
        value: 'approved_idea',
    },
    {
        title: t('idea'),
        value: 'idea',
    },
];

const status2Options = computed<{ value: ProjectStatus2; title: string }[]>(
    () => [
        {
            title: t('active'),
            value: ProjectStatus2.ACTIVE,
        },
        {
            title: t('on_hold'),
            value: ProjectStatus2.ON_HOLD,
        },
        {
            title: t('finished'),
            value: ProjectStatus2.FINISHED,
            props: {
                disabled: !hasPermission(
                    PolicyAction.FINISH_PROJECT,
                    project?.id,
                    project?.masterPortfolio?.id
                ),
            },
        },
    ]
);

const { mutate: updateStatus, onError: onStatusUpdateError } = useMutation(
    graphql(`
        mutation UpdateStatus($input: UpdateProjectStatusInput!) {
            updateProjectStatus(input: $input) {
                ok
            }
        }
    `),
    {
        refetchQueries: [
            'Project',
            'ProjectDetails',
            'MetroProjects',
            'ProgramProjects',
            'Program',
            'Projects',
            'Ideas',
        ],
    }
);

async function handleStatusUpdate(value: ProjectStatus) {
    try {
        await updateStatus({
            input: {
                projectId: projectId.value,
                status: value,
            },
        });
        notificationStore.add({
            type: 'success',
            message: t('status_updated', { status: t(value) }),
        });
    } catch {
        notificationStore.add({
            type: 'error',
            message: t('error_updating_status', { status: t(value) }),
        });
    }
}

onStatusUpdateError((value) => {
    alert(value);
});

const { mutate: finishProject } = useMutation(
    graphql(`
        mutation FinishProject($input: FinishProjectInput!) {
            finishProject(input: $input) {
                ok
            }
        }
    `),
    {
        refetchQueries: [
            'Project',
            'ProjectDetails',
            'MetroProjects',
            'ProgramProjects',
            'Program',
            'Projects',
            'Ideas',
        ],
    }
);

const refetchHoldQueries = [
    'Project',
    'ProjectDetails',
    'SingleProject',
    'MetroProjects',
    'ProgramProjects',
    'Program',
    'Projects',
    'Ideas',
];

const { mutate: holdProject } = useMutation(
    graphql(`
        mutation HoldProject($input: HoldProjectInput!) {
            holdProject(input: $input) {
                project {
                    onHold
                }
            }
        }
    `),
    {
        refetchQueries: refetchHoldQueries,
        awaitRefetchQueries: true,
    }
);

const { mutate: unholdProject } = useMutation(
    graphql(`
        mutation UnholdProject($input: UnholdProjectInput!) {
            unholdProject(input: $input) {
                project {
                    onHold
                }
            }
        }
    `),
    {
        refetchQueries: refetchHoldQueries,
        awaitRefetchQueries: true,
    }
);

async function handleStatus2Update(value: ProjectStatus2) {
    try {
        if (value === ProjectStatus2.FINISHED) {
            await finishProject({
                input: {
                    projectId: projectId.value,
                },
            });
            status2.value = value;
            confetti();
            notificationStore.add({
                type: 'success',
                message: t('project_finished'),
            });
        }

        if (value === ProjectStatus2.ON_HOLD) {
            isShowingHoldModal.value = true;
            return;
        }

        if (value === ProjectStatus2.ACTIVE) {
            await unholdProject({
                input: {
                    projectId: projectId.value,
                },
            });
            status2.value = value;
            notificationStore.add({
                type: 'success',
                message: t('project_active'),
            });
        }
    } catch {
        notificationStore.add({
            type: 'error',
            message: t('error_updating_status', { status: value }),
        });
    }
}

async function onHoldConfirmed(values: ProjectHoldFormValues) {
    try {
        await holdProject({
            input: {
                projectId: projectId.value,
                reason: values.reason,
            },
        });
        status2.value = ProjectStatus2.ON_HOLD;
        isShowingHoldModal.value = false;
        notificationStore.add({
            type: 'success',
            message: t('project_on_hold'),
        });
    } catch {
        notificationStore.add({
            type: 'error',
            message: t('error_updating_status', {
                status: t('on_hold'),
            }),
        });
    }
}

const canBeConverted = computed(
    () =>
        !isSubproject &&
        project &&
        project.masterPortfolio?.id === activePortfolioId.value &&
        (hasPermission(
            PolicyAction.CONVERT_SUBPROJECT,
            project.id,
            project.masterPortfolio?.id
        ) ||
            hasPermission(
                PolicyAction.CONVERT_SUBPROJECT,
                project?.parentProjectId ?? undefined,
                project?.masterPortfolio?.id ?? undefined
            ))
);
const canBeUpdated = computed(
    () =>
        hasPermission(
            PolicyAction.PROJECT_WRITE,
            project?.id,
            project?.masterPortfolio?.id ?? undefined
        ) ||
        hasPermission(
            PolicyAction.PROJECT_WRITE,
            project?.id,
            activePortfolioId.value ?? undefined
        ) ||
        hasPermission(
            PolicyAction.PROJECT_WRITE,
            project?.parentProjectId ?? undefined,
            project?.masterPortfolio?.id ?? undefined
        ) ||
        hasPermission(
            PolicyAction.PROJECT_WRITE,
            project?.parentProjectId ?? undefined,
            activePortfolioId.value ?? undefined
        )
);
const canBeConvertedBack = computed(
    () =>
        isSubproject &&
        project &&
        project.masterPortfolio?.id === activePortfolioId.value &&
        (hasPermission(
            PolicyAction.CONVERT_SUBPROJECT,
            project.id,
            project.masterPortfolio?.id
        ) ||
            hasPermission(
                PolicyAction.CONVERT_SUBPROJECT,
                project?.parentProjectId ?? undefined,
                project?.masterPortfolio?.id ?? undefined
            ))
);
const canHoldProject = computed(
    () =>
        hasPermission(
            PolicyAction.HOLD_PROJECT,
            project?.id,
            project?.masterPortfolio?.id
        ) ||
        hasPermission(
            PolicyAction.HOLD_PROJECT,
            project?.id,
            activePortfolioId.value ?? undefined
        ) ||
        hasPermission(
            PolicyAction.HOLD_PROJECT,
            project?.parentProjectId ?? undefined,
            project?.masterPortfolio?.id ?? undefined
        ) ||
        hasPermission(
            PolicyAction.HOLD_PROJECT,
            project?.parentProjectId ?? undefined,
            activePortfolioId.value ?? undefined
        )
);
const canMoveProject = computed(
    () =>
        hasPermission(
            PolicyAction.PROJECT_WRITE,
            project?.id,
            project?.masterPortfolio?.id
        ) ||
        hasPermission(
            PolicyAction.PROJECT_WRITE,
            project?.parentProjectId ?? undefined,
            project?.masterPortfolio?.id ?? undefined
        )
);
const canDeleteProject = computed(
    () =>
        hasPermission(
            PolicyAction.PROJECT_DELETE,
            project?.id,
            project?.masterPortfolio?.id
        ) ||
        hasPermission(
            PolicyAction.PROJECT_DELETE,
            project?.parentProjectId ?? undefined,
            project?.masterPortfolio?.id ?? undefined
        )
);
</script>
<template>
    <ProjectHoldModal
        v-model="isShowingHoldModal"
        :project-id="project.id"
        @submit="onHoldConfirmed"
    />
    <v-select
        v-model="status"
        :disabled="
            !hasPermission(
                PolicyAction.UPDATE_PROJECT_STATUS,
                project?.id,
                project?.masterPortfolio?.id
            )
        "
        theme="dark"
        hide-details
        :items="statusOptions"
        max-width="180px"
        @update:model-value="handleStatusUpdate"
    ></v-select>
    <v-select
        :model-value="status2"
        :disabled="!canHoldProject"
        theme="dark"
        hide-details
        :items="status2Options"
        max-width="180px"
        @update:model-value="handleStatus2Update"
    ></v-select>
    <v-menu v-if="canBeUpdated" :menu-props="{ closeOnContentClick: true }">
        <template #activator="{ props: activatorProps }">
            <v-btn
                theme="dark"
                icon="$more"
                variant="outlined"
                :style="{ '--v-btn-height': '2.25rem' }"
                v-bind="activatorProps"
            ></v-btn>
        </template>
        <v-list>
            <v-list-item :disabled="loading" @click="emit('edit')">
                {{ t('edit') }}
            </v-list-item>
            <template v-if="typeof projectId === 'string'">
                <ProjectConvertModal
                    v-if="canBeConverted"
                    :project-id
                    :portfolio-id="activePortfolioId"
                >
                    <template #activator="{ props }">
                        <v-list-item v-bind="props">
                            {{ t('convert') }}
                        </v-list-item>
                    </template>
                </ProjectConvertModal>
            </template>
            <template v-if="typeof projectId === 'string'">
                <SubprojectConvertDialog v-if="canBeConvertedBack" :project-id>
                    <template #activator="{ props }">
                        <v-list-item v-bind="props">{{
                            t('convert_back')
                        }}</v-list-item>
                    </template>
                </SubprojectConvertDialog>
            </template>
            <template v-if="typeof projectId === 'string'">
                <ProjectMoveModal
                    v-if="canMoveProject"
                    :project-id
                    :focus-id
                    :need-id
                    :portfolio-id="String(project?.masterPortfolio?.id)"
                >
                    <template #activator="{ props }">
                        <v-list-item v-bind="props">
                            {{ t('move') }}
                        </v-list-item>
                    </template>
                </ProjectMoveModal>
            </template>
            <template v-if="typeof projectId === 'string'">
                <ProjectDeleteDialog
                    v-if="canDeleteProject"
                    :project-id
                    @success="onProjectDeleted"
                >
                    <template #activator="{ props }">
                        <v-list-item v-bind="props">{{
                            t('delete')
                        }}</v-list-item>
                    </template>
                </ProjectDeleteDialog>
            </template>
        </v-list>
    </v-menu>
</template>
